/**
 * Modèle de contenu du Centre d'aide PexiTalk.
 *
 * La documentation est DONNÉE (pas du HTML codé en dur) : un arbre de sections
 * (« grandes lignes ») → articles → sous-articles (détail de plus en plus fin).
 * Chaque nœud porte un `minRole` : l'accès est filtré selon le rôle de
 * l'utilisateur connecté (un agent ne voit pas les pages d'administration).
 *
 * Le rendu est assuré par `DocBlocks.vue` à partir des `Block[]`. Le texte des
 * blocs accepte un balisage INLINE minimal interprété par le renderer :
 *   - **gras**            → <strong>
 *   - `code`              → <code>
 *   - [[sectionId/articleId|libellé]] → lien interne vers un autre article
 */

export type RoleId = 'agent' | 'team_admin' | 'admin' | 'owner'

/** Rang hiérarchique : un rôle voit tout ce dont le `minRole` est ≤ son rang. */
export const ROLE_RANK: Record<RoleId, number> = {
  agent: 1,
  team_admin: 2,
  admin: 3,
  owner: 4,
}

export const ROLE_LABEL: Record<RoleId, string> = {
  agent: 'Agent',
  team_admin: 'Superviseur',
  admin: 'Administrateur',
  owner: 'Propriétaire',
}

export type Block =
  /** Paragraphe de texte (balisage inline supporté). */
  | { type: 'p'; text: string }
  /** Sous-titre à l'intérieur d'un article. */
  | { type: 'h'; text: string }
  /** Étapes numérotées (procédure « comment faire »). */
  | { type: 'steps'; items: string[] }
  /** Liste à puces. */
  | { type: 'list'; items: string[] }
  /** Encart mis en valeur. */
  | { type: 'callout'; variant: 'info' | 'tip' | 'warning' | 'danger'; title?: string; text: string }
  /** Tableau de référence. */
  | { type: 'table'; headers: string[]; rows: string[][] }
  /** Bloc de code / configuration (monospace). */
  | { type: 'code'; lang?: string; code: string }
  /** Paires clé→valeur (ex. liste des statuts, des champs d'un formulaire). */
  | { type: 'kv'; items: { k: string; v: string }[] }
  /** Foire aux questions / dépannage. */
  | { type: 'faq'; items: { q: string; a: string }[] }
  /** Renvois vers d'autres articles (cartes cliquables). */
  | { type: 'seealso'; items: { to: string; label: string }[] }
  /**
   * Illustration / capture d'écran. Fournir SOIT `svg` (maquette intégrée),
   * SOIT `src` (vraie capture déposée dans /public ou une URL). `alt` est
   * obligatoire (accessibilité) ; `caption` est la légende affichée.
   */
  | { type: 'figure'; svg?: string; src?: string; alt: string; caption?: string }

export interface DocArticle {
  id: string
  title: string
  /** Résumé court affiché dans la liste et en tête d'article. */
  summary?: string
  icon?: string
  /** Rôle minimum requis. Hérite de la section si absent. */
  minRole?: RoleId
  blocks: Block[]
  /** Sous-articles : un niveau de détail supplémentaire. */
  children?: DocArticle[]
}

export interface DocSection {
  id: string
  title: string
  icon: string
  summary: string
  /** Couleur d'accent de la carte (hex). */
  accent?: string
  /** Rôle minimum pour VOIR la grande ligne. Défaut : agent. */
  minRole?: RoleId
  articles: DocArticle[]
}
